import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import { Button, CircularProgress, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Box } from '@/components/ui'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { MainLayout, PageContent } from '@/layouts'
import { spacing } from '@/themes/tokens'

import { FeatureFlagList } from './components/FeatureFlagList'
import { GlobalSwitchesForm } from './components/GlobalSwitchesForm'
import { RolloutPanel } from './components/RolloutPanel'

const TwoColumnLayout = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(spacing.xl),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
}))

/**
 * The list holds still while the flag beside it is worked on.
 *
 * A definite `height` rather than a `maxHeight`: the list inside scrolls by
 * shrinking below its content, and a percentage height against a content-sized
 * column resolves to `auto`, which would leave the list at full height and
 * spilling out of the column instead.
 */
const ListColumn = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    flex: '0 0 300px',
    position: 'sticky',
    top: theme.spacing(spacing.lg),
    height: '80vh',
  },
}))

const DetailColumn = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(spacing.lg),
  [theme.breakpoints.up('md')]: {
    flex: 1,
    minWidth: 0,
  },
}))

/**
 * The feature flag registry, and the rollout for whichever flag is selected.
 *
 * The selected flag lives in the URL so a link to one can be handed to whoever
 * asked for the rollout. The form is keyed on it, so moving to another flag
 * rebuilds from that flag's stored switches rather than syncing new values into
 * form state already holding somebody else's.
 */
export const FeatureFlagsPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')

  const { data: flags, isLoading, isError } = useFeatureFlags()

  const selectedName = searchParams.get('flag')
  const selected = flags?.find((flag) => flag.name === selectedName) ?? null

  const handleSelect = (name: string) => {
    setSearchParams({ flag: name }, { replace: true })
  }

  return (
    <MainLayout
      title='Feature Flags'
      subtitle={isLoading ? undefined : `${flags?.length ?? 0} flags`}
      actions={
        <Button
          variant='outlined'
          size='medium'
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate('/orgs')}
        >
          Organizations
        </Button>
      }
    >
      <PageContent>
        {isLoading && (
          <Box variant='flexCenter' py={8}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Typography color='text.secondary'>
            Feature flags could not be loaded.
          </Typography>
        )}

        {flags !== undefined && (
          <TwoColumnLayout>
            <ListColumn>
              <FeatureFlagList
                flags={flags}
                selectedName={selectedName}
                search={search}
                onSearchChange={setSearch}
                onSelect={handleSelect}
              />
            </ListColumn>

            <DetailColumn>
              {selected === null ? (
                <Typography variant='body2' color='text.secondary'>
                  Select a flag to change its switches or roll it out.
                </Typography>
              ) : (
                <>
                  <GlobalSwitchesForm key={selected.name} flag={selected} />
                  <RolloutPanel key={`rollout-${selected.name}`} flag={selected} />
                </>
              )}
            </DetailColumn>
          </TwoColumnLayout>
        )}
      </PageContent>
    </MainLayout>
  )
}
